import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { accountEndpoints, fantasyEndpoints } from '@pff-consumer/api-sdk'
import { envConfig } from '@pff-consumer/utils'
import type { LeaguePreview, LeagueSettings } from '@pff-consumer/schema'
import { AccountsResponseV2, League, LeagueUpsert } from '@pff-consumer/schema'
import { isHydrateAction } from '../helpers/hydrateAction'

const { CONSUMER_API_URL, CONSUMER_API_MIDDLEWARE_URL, CONSUMER_API_KEY } = envConfig

const getAuthTokenFromState = (state: unknown): string | undefined => {
  // @ts-ignore
  return state?.account?.token
}

const baseUrl = process.env.NODE_ENV === 'development' ? `${CONSUMER_API_MIDDLEWARE_URL}/` : '/'

// Define a service using a base URL and expected endpoints
export const fantasySyncApi = createApi({
  reducerPath: 'fantasySyncApi',
  tagTypes: ['FantasySyncLeagues'],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      // We need to get the auth/jwt token from the store and add to the headers for every api call
      const authToken = getAuthTokenFromState(getState())
      if (authToken) {
        headers.append('Authorization', authToken)
      }
      headers.append('Api-Key', CONSUMER_API_KEY)

      return headers
    },
  }),
  extractRehydrationInfo(action, { reducerPath }) {
    if (isHydrateAction(action)) {
      return action.payload[reducerPath]
    }
  },
  endpoints: (builder) => ({
    createUserAccount: builder.mutation<
      AccountsResponseV2,
      { email: string; password: string; shouldSubscribeToNewsletter: boolean; recaptchaToken: string }
    >({
      query: (params) => ({
        url: `${CONSUMER_API_URL}${accountEndpoints.recaptchaCreateAccount().url}`,
        method: 'post',
        body: {
          email: params.email,
          password: params.password,
          shouldSubscribeToNewsletter: params.shouldSubscribeToNewsletter,
          recaptchaToken: params.recaptchaToken,
        },
        headers: {
          'Content-type': 'application/json',
        },
      }),
    }),
    getMyLeagues: builder.query<{ leagues: League[] }, {}>({
      query: fantasyEndpoints.getMyLeagues,
      providesTags: ['FantasySyncLeagues'],
    }),
    getSleeperLeagues: builder.query<{ userLeagues: LeaguePreview[] }, string>({
      query: fantasyEndpoints.getSleeperLeagues,
    }),
    getEspnLeagues: builder.query<{ userLeagues: LeaguePreview[] }, string>({
      query: fantasyEndpoints.getEspnLeagues,
    }),
    getYahooLeagues: builder.query<{ userLeagues: LeaguePreview[] }, string>({
      query: fantasyEndpoints.getYahooLeagues,
    }),
    deleteMyLeague: builder.mutation<{}, { leagueId: string }>({
      query: ({ leagueId }) => ({
        url: `${baseUrl}${fantasyEndpoints.deleteLeague(leagueId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FantasySyncLeagues'],
    }),
    resyncFantasyLeague: builder.mutation<{ league: League }, { leagueId: string }>({
      invalidatesTags: ['FantasySyncLeagues'],
      query: ({ leagueId }) => ({
        url: `${baseUrl}${fantasyEndpoints.resyncFantasyLeague(leagueId)}`,
        method: 'POST',
        body: {},
      }),
    }),
    syncSleeperLeague: builder.mutation<{ league: League }, { sleeperUserId: string; sleeperLeagueId: string }>({
      query: ({ sleeperUserId, sleeperLeagueId }) => ({
        url: `${baseUrl}${fantasyEndpoints.syncSleeperLeague(sleeperLeagueId)}`,
        method: 'POST',
        body: {
          sleeper_user_id: sleeperUserId,
        },
      }),
    }),
    syncYahooLeague: builder.mutation<{ league: League } | { authorizeUrl: string }, { hostedLeagueUrl: string }>({
      query: ({ hostedLeagueUrl }) => ({
        url: `${baseUrl}${fantasyEndpoints.syncYahooLeague()}`,
        method: 'POST',
        body: {
          hosted_league_url: hostedLeagueUrl,
        },
      }),
    }),
    syncEspnLeague: builder.mutation<{ league: League }, { hostedLeagueUrl: string }>({
      query: ({ hostedLeagueUrl }) => ({
        url: `${baseUrl}${fantasyEndpoints.syncEspnLeague()}`,
        method: 'POST',
        body: {
          hosted_league_url: hostedLeagueUrl,
        },
      }),
    }),
    createManualLeague: builder.mutation<{}, { data: { league: LeagueUpsert; league_settings: LeagueSettings } }>({
      query: ({ data }) => ({
        url: `${baseUrl}${fantasyEndpoints.createManualLeague()}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['FantasySyncLeagues'],
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useCreateUserAccountMutation,
  useGetMyLeaguesQuery,
  useDeleteMyLeagueMutation,
  useResyncFantasyLeagueMutation,
  useCreateManualLeagueMutation,
  useGetSleeperLeaguesQuery,
  useGetEspnLeaguesQuery,
  useGetYahooLeaguesQuery,
  useSyncSleeperLeagueMutation,
  useSyncYahooLeagueMutation,
  useSyncEspnLeagueMutation,
} = fantasySyncApi

// Export getters to be used when rendering components on the server
export const {
  createUserAccount,
  getMyLeagues,
  deleteMyLeague,
  resyncFantasyLeague,
  createManualLeague,
  getSleeperLeagues,
  getEspnLeagues,
  getYahooLeagues,
  syncSleeperLeague,
  syncYahooLeague,
  syncEspnLeague,
} = fantasySyncApi.endpoints
